/** Home screen: onboarding, matchmaking, and private-match entry points. */

import { useEffect, useState, type CSSProperties, type ReactElement } from "react";
import { toModeId, type ModeId, type QueueSpec } from "@pit/protocol";
import { ChampionLoadoutPicker } from "../champions/ChampionLoadoutPicker.js";
import { useChampionLoadoutState } from "../champions/useChampionLoadoutState.js";
import { useWsClient } from "../net/WsClientProvider.js";
import { LIVE_MAP_SLUG } from "../render/arenaCatalog.js";
import { navigate } from "../router/hashRoute.js";
import { useStore } from "../store/context.js";
import { MapPicker } from "./MapPicker.js";
import { ModePicker } from "./ModePicker.js";
import { extractMatchId } from "./extractMatchId.js";

const DEFAULT_MODE_ID: ModeId = toModeId("1v1");

// The app shell is viewport-bounded. This element deliberately owns scrolling
// so a short viewport can always reach every home-screen control (#769).
const HOME_ROOT_STYLE: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-start",
  width: "100%",
  height: "100%",
  overflowY: "auto",
};
const HOME_CONTENT_STYLE: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  width: "100%",
  maxWidth: 760,
  margin: "auto",
  gap: "clamp(10px, 2.2vh, 24px)",
  padding: "clamp(12px, 3vh, 32px)",
};
const HERO_STYLE: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 6,
  textAlign: "center",
};
const DESCRIPTION_STYLE: CSSProperties = {
  maxWidth: 430,
  margin: 0,
  color: "#aebbc9",
  fontSize: 14,
  lineHeight: 1.45,
};
const SECTION_STYLE: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  width: "100%",
  gap: 14,
  padding: "clamp(14px, 2vw, 20px)",
  border: "1px solid #2a3542",
  borderRadius: 10,
  background: "rgba(22, 28, 36, 0.72)",
};
const SECTION_HEADING_STYLE: CSSProperties = {
  margin: 0,
  color: "#dfe6ef",
  fontSize: 14,
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
};
const CONTROL_ROW_STYLE: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  justifyContent: "center",
  gap: 12,
};
const PRIMARY_CTA_STYLE: CSSProperties = {
  padding: "12px 32px",
  border: "none",
  borderRadius: 6,
  background: "#3aa675",
  color: "#fff",
  display: "inline-block",
  fontSize: 18,
  fontWeight: 700,
  textAlign: "center",
  textDecoration: "none",
};
const MATCH_CTA_STYLE: CSSProperties = {
  ...PRIMARY_CTA_STYLE,
  border: "2px solid #3aa675",
  background: "transparent",
  color: "#7fd3ae",
};
const SECONDARY_CTA_STYLE: CSSProperties = {
  padding: "10px 18px",
  border: "1px solid #3a4757",
  borderRadius: 6,
  background: "#2a3340",
  color: "#fff",
  fontSize: 15,
};
const UTILITY_LINK_STYLE: CSSProperties = { fontSize: 13, textDecoration: "none" };

export function HomeScreen(): ReactElement {
  const { state, dispatch } = useStore();
  const client = useWsClient();
  const [joinInput, setJoinInput] = useState("");
  const [pending, setPending] = useState<"create" | "none">("none");
  const [modeId, setModeId] = useState<ModeId>(DEFAULT_MODE_ID);
  const loadoutState = useChampionLoadoutState();
  const { championId, loadout, isValid: loadoutValid } = loadoutState;
  const [mapSlug, setMapSlug] = useState(LIVE_MAP_SLUG);
  const canConnect = state.connection.kind === "welcomed";

  useEffect(() => {
    if (pending === "create" && state.matchId !== null && state.joinUrl !== null) {
      navigate({ kind: "match" });
      setPending("none");
    }
  }, [pending, state.matchId, state.joinUrl]);
  useEffect(() => {
    if (pending === "create" && state.serverError !== null) setPending("none");
  }, [pending, state.serverError]);

  return (
    <div data-testid="home-root" style={HOME_ROOT_STYLE}>
      <main data-testid="home-content" style={HOME_CONTENT_STYLE}>
        <header style={HERO_STYLE}>
          <h1 style={{ margin: 0 }}>Pit</h1>
          <p data-testid="home-description" style={DESCRIPTION_STYLE}>
            A fast, deterministic browser arena PvP game. Choose a champion, master your loadout,
            and outplay your opponents.
          </p>
        </header>
        <section
          aria-labelledby="start-here-heading"
          style={{ ...SECTION_STYLE, alignItems: "center" }}
        >
          <h2 id="start-here-heading" style={SECTION_HEADING_STYLE}>
            Start here
          </h2>
          <div style={{ color: "#aebbc9", fontSize: 13, textAlign: "center" }}>
            New to Pit? Learn the arena before you queue.
          </div>
          <a
            href="#/practice-dummy"
            data-testid="home-dummy-practice-link"
            style={PRIMARY_CTA_STYLE}
          >
            Tutorial
          </a>
        </section>
        <section aria-labelledby="play-match-heading" style={SECTION_STYLE}>
          <h2 id="play-match-heading" style={SECTION_HEADING_STYLE}>
            Play a match
          </h2>
          <ChampionLoadoutPicker
            state={loadoutState}
            disabled={!canConnect}
            layoutStyle={{ gap: 14 }}
          />
          <div style={CONTROL_ROW_STYLE}>
            <ModePicker value={modeId} onChange={setModeId} disabled={!canConnect} />
            <button
              type="button"
              data-testid="home-quick-match"
              disabled={!canConnect || !loadoutValid}
              onClick={() => {
                if (state.matchId !== null) dispatch({ type: "match.leave" });
                dispatch({ type: "match.champion", championId, loadout });
                const queue: QueueSpec = { mode: modeId, ranked: false };
                client.quickJoin(queue, championId, [...loadout]);
                setPending("create");
              }}
              style={{
                ...MATCH_CTA_STYLE,
                color: canConnect ? "#7fd3ae" : "#6b7480",
                borderColor: canConnect ? "#3aa675" : "#333",
              }}
            >
              Quick match
            </button>
          </div>
        </section>
        <section aria-labelledby="private-match-heading" style={SECTION_STYLE}>
          <h2 id="private-match-heading" style={SECTION_HEADING_STYLE}>
            Private match
          </h2>
          <div style={CONTROL_ROW_STYLE}>
            <MapPicker value={mapSlug} onChange={setMapSlug} disabled={!canConnect} />
            <button
              type="button"
              data-testid="home-create"
              disabled={!canConnect || !loadoutValid}
              onClick={() => {
                if (state.matchId !== null) dispatch({ type: "match.leave" });
                dispatch({ type: "match.champion", championId, loadout });
                client.createMatch(championId, [...loadout], { mapSlug });
                setPending("create");
              }}
              style={{ ...SECONDARY_CTA_STYLE, background: canConnect ? "#2a3340" : "#333" }}
            >
              Create private match
            </button>
          </div>
          <div style={{ borderTop: "1px solid #2a3542" }} />
          <form
            onSubmit={(event) => {
              event.preventDefault();
              const id = extractMatchId(joinInput);
              if (id !== null) navigate({ kind: "join", matchId: id });
            }}
            style={CONTROL_ROW_STYLE}
          >
            <label style={{ color: "#aebbc9", fontSize: 14 }}>
              Join with a link
              <input
                type="text"
                value={joinInput}
                onChange={(event) => {
                  setJoinInput(event.target.value);
                }}
                placeholder="matchId or full URL"
                data-testid="home-join-input"
                style={{
                  marginLeft: 8,
                  padding: "8px 12px",
                  border: "1px solid #3a4757",
                  borderRadius: 4,
                  background: "#10151b",
                  color: "#dfe6ef",
                  fontSize: 16,
                  minWidth: "min(280px, 65vw)",
                }}
              />
            </label>
            <button
              type="submit"
              data-testid="home-join-submit"
              disabled={!canConnect || extractMatchId(joinInput) === null}
              style={{ ...SECONDARY_CTA_STYLE, background: "#5680d6", border: "none" }}
            >
              Join
            </button>
          </form>
        </section>
        <nav aria-label="Utilities" style={CONTROL_ROW_STYLE}>
          <a href="#/account" data-testid="home-account-link" style={UTILITY_LINK_STYLE}>
            Account — sign in or save guest
          </a>
          <a href="#/settings" data-testid="home-settings-link" style={UTILITY_LINK_STYLE}>
            ⚙ Settings
          </a>
          <a href="#/practice" data-testid="home-practice-link" style={UTILITY_LINK_STYLE}>
            Practice mode (1v1 / 3v3 vs bots)
          </a>
        </nav>
        {state.connection.kind === "connecting" ? (
          <div style={{ opacity: 0.7 }}>connecting…</div>
        ) : null}
        {import.meta.env.DEV ? (
          <a
            href="#/demo"
            data-testid="home-demo-link"
            style={{ ...UTILITY_LINK_STYLE, opacity: 0.5 }}
          >
            dev demo (no server)
          </a>
        ) : null}
      </main>
    </div>
  );
}
